import { useReducer, useEffect, useCallback, useRef } from 'react';
import {
  DashboardService,
  DashboardResponse,
} from '../../../service/DashboardService';
import { AuthService } from '../../../service/AuthService';
import { useAuthStore } from '../../../store/storeAuth';
import { IGetOneResponse } from '../../../util/Types/Generic';
import {
  dashboardManagerReducer,
  dashboardManagerActions,
  initialDashboardManagerState,
  DashboardMeta,
} from '../Reducer/dashboardManagerReducer';

export type { DashboardMeta };

function getErrorStatusCode(error: object): number | undefined {
  if (
    'statusCode' in error &&
    typeof (error as { statusCode: number }).statusCode === 'number'
  ) {
    return (error as { statusCode: number }).statusCode;
  }
  return undefined;
}

function extractDashboardList(
  response: IGetOneResponse<DashboardResponse[]> | DashboardResponse[],
): DashboardResponse[] {
  if (Array.isArray(response)) {
    return response;
  }
  if (Array.isArray(response.data)) {
    return response.data;
  }
  return [];
}

export function useDashboardManager() {
  const [state, dispatch] = useReducer(
    dashboardManagerReducer,
    initialDashboardManagerState,
  );
  const setAuthUser = useAuthStore(s => s.setAuthUser);
  const selectedCompanyKey = useAuthStore(
    s => s.user?.selectedCompanyId ?? null,
  );
  const previousCompanyScopeRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    const loadDashboards = async () => {
      const key = selectedCompanyKey;
      const hadPreviousScope = previousCompanyScopeRef.current !== undefined;
      const companyScopeChanged =
        hadPreviousScope && previousCompanyScopeRef.current !== key;
      previousCompanyScopeRef.current = key;

      if (companyScopeChanged) {
        dispatch(dashboardManagerActions.resetForCompanyContext());
      } else if (!hadPreviousScope) {
        dispatch(dashboardManagerActions.setLoading(true));
      }

      try {
        const response = await DashboardService.getAllDashboards();
        const dashboardsData = extractDashboardList(
          response as
            | IGetOneResponse<DashboardResponse[]>
            | DashboardResponse[],
        );

        const dashboards: DashboardMeta[] = dashboardsData.map(d => ({
          id: d.id,
          name: d.name || 'Untitled Dashboard',
          status: d.status || 'draft',
          isDefault: d.isDefault ?? false,
        }));

        dispatch(dashboardManagerActions.setDashboards(dashboards));
      } catch (error) {
        console.error('Error loading dashboards:', error);
        if (
          error &&
          typeof error === 'object' &&
          getErrorStatusCode(error) === 403
        ) {
          try {
            const res = await AuthService.getMe();
            setAuthUser(res.data);
          } catch (syncError) {
            console.error(syncError);
          }
          dispatch(
            dashboardManagerActions.setError(
              'Dashboard access denied for this workspace. Switch organization or try again.',
            ),
          );
        } else {
          dispatch(
            dashboardManagerActions.setError('Failed to load dashboards'),
          );
        }
      }
    };

    loadDashboards();
  }, [selectedCompanyKey]);

  const createDashboard = useCallback(
    async (name: string = 'New Dashboard') => {
      dispatch(dashboardManagerActions.setCreating(true));

      try {
        const createResponse = await DashboardService.createDashboard();

        const updateResponse = await DashboardService.updateDashboard(
          createResponse.data.id,
          { name },
        );

        const newDashboard: DashboardMeta = {
          id: updateResponse.data.id,
          name: updateResponse.data.name || name,
          status: updateResponse.data.status,
          isDefault: updateResponse.data.isDefault || false,
        };

        dispatch(dashboardManagerActions.addDashboard(newDashboard));
        return newDashboard;
      } catch (error) {
        console.error('Error creating dashboard:', error);
        dispatch(dashboardManagerActions.setCreating(false));
        throw error;
      }
    },
    [],
  );

  const selectDashboard = useCallback((dashboardId: string) => {
    dispatch(dashboardManagerActions.setActiveDashboard(dashboardId));
  }, []);

  const renameDashboard = useCallback(
    async (dashboardId: string, newName: string) => {
      dispatch(dashboardManagerActions.setRenaming(true));

      const currentDashboard = state.dashboards.find(d => d.id === dashboardId);

      try {
        const response = await DashboardService.updateDashboard(dashboardId, {
          name: newName,
        });
        const updatedDashboard: DashboardMeta = {
          id: response.data.id,
          name: response.data.name || newName,
          status: currentDashboard?.status || response.data.status,
          isDefault:
            currentDashboard?.isDefault || response.data.isDefault || false,
        };

        dispatch(dashboardManagerActions.updateDashboard(updatedDashboard));
      } catch (error) {
        console.error('Error renaming dashboard:', error);
        dispatch(
          dashboardManagerActions.setError('Failed to rename dashboard'),
        );
        throw error;
      }
    },
    [state.dashboards],
  );

  const deleteDashboard = useCallback(
    async (dashboardId: string) => {
      if (state.dashboards.length <= 1) {
        throw new Error('Cannot delete the last dashboard');
      }

      dispatch(dashboardManagerActions.setDeleting(true));

      try {
        await DashboardService.deleteDashboard(dashboardId);
        dispatch(dashboardManagerActions.removeDashboard(dashboardId));
      } catch (error) {
        console.error('Error deleting dashboard:', error);
        dispatch(
          dashboardManagerActions.setError('Failed to delete dashboard'),
        );
        throw error;
      }
    },
    [state.dashboards.length],
  );

  const publishDashboard = useCallback(
    async (dashboardId: string, publish: boolean) => {
      dispatch(dashboardManagerActions.setPublishing(true));

      const currentDashboard = state.dashboards.find(d => d.id === dashboardId);

      try {
        const newStatus = publish ? 'published' : 'draft';
        const response = await DashboardService.updateDashboard(dashboardId, {
          status: newStatus,
        });
        const updatedDashboard: DashboardMeta = {
          id: response.data.id,
          name:
            currentDashboard?.name ||
            response.data.name ||
            'Untitled Dashboard',
          status: response.data.status,
          isDefault:
            currentDashboard?.isDefault || response.data.isDefault || false,
        };

        dispatch(dashboardManagerActions.updateDashboard(updatedDashboard));
      } catch (error) {
        console.error('Error publishing dashboard:', error);
        dispatch(
          dashboardManagerActions.setError('Failed to update dashboard status'),
        );
        throw error;
      }
    },
    [state.dashboards],
  );

  const setDefaultDashboard = useCallback(
    async (dashboardId: string) => {
      const currentDashboard = state.dashboards.find(d => d.id === dashboardId);
      if (!currentDashboard) return;

      try {
        const updatedDashboards = state.dashboards.map(d => ({
          ...d,
          isDefault: d.id === dashboardId,
        }));

        await Promise.all(
          updatedDashboards.map(d =>
            DashboardService.updateDashboard(d.id, { isDefault: d.isDefault }),
          ),
        );

        updatedDashboards.forEach(d => {
          dispatch(dashboardManagerActions.updateDashboard(d));
        });
      } catch (error) {
        console.error('Error setting default dashboard:', error);
        dispatch(
          dashboardManagerActions.setError('Failed to set default dashboard'),
        );
        throw error;
      }
    },
    [state.dashboards],
  );

  const activeDashboard =
    state.dashboards.find(d => d.id === state.activeDashboardId) || null;

  return {
    dashboards: state.dashboards,
    activeDashboard,
    activeDashboardId: state.activeDashboardId,
    isLoading: state.isLoading,
    isCreating: state.isCreating,
    isDeleting: state.isDeleting,
    isRenaming: state.isRenaming,
    isPublishing: state.isPublishing,
    error: state.error,
    createDashboard,
    selectDashboard,
    renameDashboard,
    deleteDashboard,
    publishDashboard,
    setDefaultDashboard,
  };
}
